export const isSuggestedElementsHaveChild = (elementId, storedPageAllHtmlData) => {
  const findParentEleDetails = storedPageAllHtmlData.find((el) => el.id === elementId);

  let isSiblingsAvailable = false;
  if (findParentEleDetails && findParentEleDetails?.siblings?.length > 0) {
    findParentEleDetails.siblings.forEach((sibElement) => {
      for (let i = 0; i < storedPageAllHtmlData.length; i++) {
        if (storedPageAllHtmlData[i].id == sibElement.id) {
          isSiblingsAvailable = true;
          break;
        }
      }
    });
  }
  return isSiblingsAvailable;
};

/**
 * The colour of the keyword-difficulty dot.
 *
 * The bands are the ones production has always drawn: 0-24 amber, 25-49
 * light orange, 50-74 orange, 75-100 red. A greener palette was tried on
 * this branch and reverted so the table reads the same as master.
 *
 * Returns '' for a value that is not a usable score, so callers render no dot
 * rather than a misleading one. Do NOT coerce a missing difficulty to 0 before
 * calling: that would paint every unmeasured keyword as the easiest there is.
 */
export const getKeywordDificultyColorRange = (score) => {
  const value = Number(score);
  if (score === null || score === undefined || score === '' || !Number.isFinite(value)) {
    return '';
  }
  switch (true) {
    case value <= 24:
      return '#EFBF2E';
    case value <= 49:
      return '#F2994A';
    case value <= 74:
      return '#E57310';
    default:
      return '#EB5757';
  }
};

export const getKeywordSuitabilityRatingColorRange = (score) => {
  switch (true) {
    case score <= 49:
      return '#E57310';
    case score > 49 && score < 80:
      return '#EFBF2E';
    case score >= 80:
      //#219653
      return '#51D497';
    default:
      return '';
  }
};

export const getKeywordTypeCount = (list) => {
  let brandedCount = 0;
  let nonBrandedCount = 0;
  // skip subkeywords
  const keywordClusterList = list.filter((kw) => !kw.sub_keyword);
  keywordClusterList.forEach((item) => {
    if (item.is_branded) {
      brandedCount++;
    } else {
      nonBrandedCount++;
    }
  });
  return {
    brandedTargetedKeywordCount: brandedCount,
    nonBrandedTargetedKeywordCount: nonBrandedCount,
  };
};

export const getHeaderString = (details, isBrandedIncluded) => {
  const countArray = [];
  if (details.nonBrandedTargetedKeywordCount) {
    countArray.push(`${details.nonBrandedTargetedKeywordCount} non-branded keywords`);
  }
  if (details.brandedTargetedKeywordCount && isBrandedIncluded) {
    countArray.push(`${details.brandedTargetedKeywordCount} branded keywords`);
  }
  if (details.newKeywordsCount) {
    countArray.push(`${details.newKeywordsCount} new non-branded keywords`);
  }
  if (details.newBrandedKeywordsCount && isBrandedIncluded) {
    countArray.push(`${details.newBrandedKeywordsCount} new branded keywords`);
  }
  return countArray.join(', ');
};

export const getPotentialHeaderString = (potentialData, isBrandedIncluded) => {
  const dataDetails = getKeywordTypeCount(potentialData);
  const countArray = [];
  if (dataDetails.nonBrandedTargetedKeywordCount) {
    countArray.push(`${dataDetails.nonBrandedTargetedKeywordCount} non-branded keywords`);
  }
  if (dataDetails.brandedTargetedKeywordCount && isBrandedIncluded) {
    countArray.push(`${dataDetails.brandedTargetedKeywordCount} branded keywords`);
  }
  return countArray.join(', ');
};

export const getRecalculateCountString = (targetdKeywordCountData) => {
  const countArray = [];
  if (targetdKeywordCountData.newKeywordsCount) {
    countArray.push(`${targetdKeywordCountData.newKeywordsCount} non-branded`);
  }
  if (targetdKeywordCountData.newBrandedKeywordsCount) {
    countArray.push(`${targetdKeywordCountData.newBrandedKeywordsCount} branded`);
  }
  return countArray.join(', ');
};

/**
 * Render a measured clicks/impressions count for the Research Keywords table.
 *
 * Impressions and clicks are monthly figures derived from a four-month window,
 * so they are genuinely fractional: a keyword served twice in four months is
 * 0.5 a month. The table used to round every such value UP, which published an
 * impression that never happened and produced the pairing the customer could
 * not reconcile, an average position of 3.5 beside "1 impression". Rounding
 * down instead would erase a keyword that really did get traffic.
 *
 * So: a whole number, rounded exactly as the page header rounds the same
 * monthly average (ROUND(SUM / 4)): 0.25 reads 0, 0.5 and 0.75 read 1, 13.25
 * reads 13. A click or an impression is a discrete event and a fraction of
 * one is not a figure a customer can act on; the header and the rows now
 * agree. The raw value still drives selection and sorting, so a keyword with
 * a single impression in the window is not treated as having none.
 */
// "5.4K", "1.5K", "2M": the row builder in cr-common has already run
// numberFormatter over anything at or above a thousand, and hands the cell a
// string. Treating that string as "not a number, so missing" printed "--" for
// a search volume of 5,400 and for 1,502 impressions while 699 rendered fine.
const K_FORMATTED = /^-?\d+(\.\d+)?[KMB]$/;

/**
 * A page-level count for the header (clicks, impressions, unique keywords):
 * the whole number the API measured, or null when it measured nothing.
 *
 * The header used to build these with `Math.floor(x) || null`, which turns a
 * measured 0 into null and renders "N/A". On Bluehost
 * /blog/cloud-hosting-vs-vps-hosting/ the page has 293 impressions a month
 * and 0 clicks, and read "Clicks N/A" as if the number were unknown. "N/A"
 * is for a page the API has no rows for; a measured zero is 0.
 */
export const wholeMeasuredCount = (value) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
    return null;
  }
  return Math.floor(Number(value));
};

export const formatMeasuredCount = (value) => {
  if (typeof value === 'string' && K_FORMATTED.test(value.trim())) {
    return value.trim();
  }
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
    return null;
  }
  return Math.round(Number(value));
};
